import Message from "src/app/models/Message";
import { getClientId, getUserId } from "src/app/modules/localstorage";
import { MESSAGE_INITIAL_PAGE_SIZE } from "../config/queryLimits";
import {
  buildChatCacheKey,
  capItems,
  deserializeTimestamp,
  mergeById,
  readChatCache,
  serializeTimestamp,
  writeChatCache,
  type StoredTimestamp,
} from "./chatStorageCache";

type StoredMessageList = {
  messages: Array<Record<string, any>>;
  newestCreatedAt: StoredTimestamp | null;
  oldestCreatedAt: StoredTimestamp | null;
  fetchedAt: number;
};

const MESSAGE_TIMESTAMP_FIELDS = [
  "createdAt",
  "updatedAt",
  "submittedAt",
  "sentAt",
  "deliveredAt",
  "failedAt",
  "readAt",
  "notifiedAt",
  "expiredAt",
] as const;

export const buildMessageStorageCacheKey = (
  collaborationId: string,
  roomId: string
) => {
  const clientId = getClientId();
  const userId = getUserId();

  if (!clientId || !userId || !collaborationId || !roomId) return null;
  return buildChatCacheKey(clientId, userId, "messages", collaborationId, roomId);
};

const serializeRef = (value: any) => {
  if (!value || typeof value !== "object") return value;
  return { id: value.id, path: value.path };
};

const serializeMessage = (message: Message) => {
  const serialized: Record<string, any> = {
    ...message,
    client: serializeRef(message.client),
    customer: serializeRef(message.customer),
    conversation: serializeRef(message.conversation),
    collaboration: serializeRef(message.collaboration),
    fromUser: serializeRef(message.fromUser),
    toUser: serializeRef(message.toUser),
    replyMessageID: serializeRef(message.replyMessageID),
    company: serializeRef(message.company),
    order: serializeRef(message.order),
    room: serializeRef(message.room),
  };

  MESSAGE_TIMESTAMP_FIELDS.forEach((field) => {
    serialized[field] = serializeTimestamp(message[field]);
  });

  return serialized;
};

const hydrateMessage = (message: Record<string, any>) => {
  const hydrated = { ...message };

  MESSAGE_TIMESTAMP_FIELDS.forEach((field) => {
    hydrated[field] = deserializeTimestamp(message[field]);
  });

  return hydrated as Message;
};

const sortByCreatedAtAsc = (messages: Message[]) => {
  return [...messages].sort(
    (a, b) => (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0)
  );
};

const latestMessagesAsc = (messages: Message[]) => {
  return sortByCreatedAtAsc(messages).slice(-MESSAGE_INITIAL_PAGE_SIZE);
};

export const getMessageCacheCursorFromData = (messages: Message[]) => {
  const sorted = sortByCreatedAtAsc(messages);

  return {
    oldestCreatedAt: sorted[0]?.createdAt ?? null,
    newestCreatedAt: sorted[sorted.length - 1]?.createdAt ?? null,
  };
};

export const getNewestMessageCreatedAt = (messages: Message[]) => {
  return getMessageCacheCursorFromData(messages).newestCreatedAt;
};

export const readStoredMessages = (
  collaborationId: string,
  roomId: string
) => {
  const key = buildMessageStorageCacheKey(collaborationId, roomId);
  if (!key) return null;

  const cached = readChatCache<StoredMessageList>(key);
  if (!cached?.messages?.length) return null;

  return {
    messages: cached.messages.map(hydrateMessage),
    newestCreatedAt: deserializeTimestamp(cached.newestCreatedAt),
    oldestCreatedAt: deserializeTimestamp(cached.oldestCreatedAt),
    fetchedAt: cached.fetchedAt,
  };
};

export const writeStoredMessages = (
  collaborationId: string,
  roomId: string,
  messages: Message[]
) => {
  const key = buildMessageStorageCacheKey(collaborationId, roomId);
  if (!key) return false;

  const capped = latestMessagesAsc(messages);
  const cursor = getMessageCacheCursorFromData(capped);

  return writeChatCache<StoredMessageList>(key, {
    messages: capped.map(serializeMessage),
    newestCreatedAt: serializeTimestamp(cursor.newestCreatedAt),
    oldestCreatedAt: serializeTimestamp(cursor.oldestCreatedAt),
    fetchedAt: Date.now(),
  });
};

export const mergeMessagesForStorage = (
  existing: Message[],
  incoming: Message[]
) => {
  return capItems(
    latestMessagesAsc(mergeById(existing as Array<Message & { id: string }>, incoming as Array<Message & { id: string }>)),
    MESSAGE_INITIAL_PAGE_SIZE
  );
};
